/** wHWmH 邮箱绑定&验证页 — /profile/email-bind（已登录、未绑定邮箱用户单码绑定）
 *
 * 流程：输入邮箱 → 获取验证码(bind/send-code) → 填 6 位 OTP → 确认绑定(bind/confirm) → 回 /profile/edit
 * spec: docs/superpowers/specs/2026-06-27-profile-email-verify-rebind-design.md §5.3
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { ApiClientError, useBindEmailSendCode, useBindEmailConfirm } from '@dogdisk/shared'
import { AppLayout } from '../../components/app-layout'
import { MobileScreen, TopBar } from '../../components/mobile-screen'

const F = 'wHWmH'
const emailSchema = z.string().email()

export function EmailBindPage() {
  const navigate = useNavigate()
  const sendCode = useBindEmailSendCode()
  const confirm = useBindEmailConfirm()
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [sent, setSent] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [ok, setOk] = useState(false)

  const describe = (e: unknown) => {
    if (e instanceof ApiClientError) {
      if (e.code === 'EMAIL_DOMAIN_NOT_ALLOWED') return '仅支持 gmail.com、qq.com 等邮箱'
      return e.message || e.code
    }
    return e instanceof Error ? e.message : String(e)
  }

  const onSend = () => {
    setErr(null)
    if (!emailSchema.safeParse(email).success) {
      setErr('请输入合法邮箱')
      return
    }
    sendCode.mutate(email, { onSuccess: () => setSent(true), onError: (e) => setErr(describe(e)) })
  }
  const onConfirm = () => {
    setErr(null)
    if (code.length !== 6) {
      setErr('请输入 6 位验证码')
      return
    }
    confirm.mutate(
      { new_email: email, code },
      {
        onSuccess: () => {
          setOk(true)
          setTimeout(() => navigate('/profile/edit'), 1200)
        },
        onError: (e) => setErr(describe(e)),
      },
    )
  }

  return (
    <AppLayout>
      <MobileScreen>
        <TopBar title="验证邮箱" fallback="/profile/edit" />
        <main data-testid={F} className="flex flex-1 flex-col gap-5 px-6 pt-4">
          <p className="text-[14px] text-[#6B7280]">绑定并验证邮箱后即可用于找回密码与安全通知。</p>

          <label className="flex flex-col gap-2">
            <span className="text-[14px] font-semibold text-[#1A1A1A]">邮箱地址</span>
            <div className="flex gap-2">
              <input
                data-testid={`${F}-email`}
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setSent(false)
                }}
                placeholder="请输入邮箱地址"
                className="h-12 flex-1 rounded-xl bg-[#F6F7F8] px-4 text-[14px] outline-none focus:ring-1 focus:ring-[#19caad]"
              />
              <button
                type="button"
                data-testid={`${F}-send`}
                disabled={sendCode.isPending}
                onClick={onSend}
                className="shrink-0 rounded-xl bg-[#19caad] px-4 text-[13px] font-semibold text-white disabled:opacity-50"
              >
                {sendCode.isPending ? '发送中…' : sent ? '重新发送' : '获取验证码'}
              </button>
            </div>
          </label>

          <label className="flex flex-col gap-2">
            <span className="text-[14px] font-semibold text-[#1A1A1A]">验证码</span>
            <input
              data-testid={`${F}-code`}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              maxLength={6}
              inputMode="numeric"
              placeholder="请输入 6 位验证码"
              className="h-12 rounded-xl bg-[#F6F7F8] px-4 text-[14px] tracking-[6px] outline-none focus:ring-1 focus:ring-[#19caad]"
            />
          </label>

          {err && <p role="alert" className="text-[13px] text-red-600">{err}</p>}
          {ok && <p role="status" className="text-[13px] text-[#16A34A]">邮箱已绑定，即将返回…</p>}

          <button
            type="button"
            data-testid={`${F}-confirm`}
            disabled={confirm.isPending || !sent}
            onClick={onConfirm}
            className="mt-2 h-12 rounded-xl bg-[#19caad] text-[15px] font-semibold text-white disabled:opacity-50"
          >
            {confirm.isPending ? '验证中…' : '确认绑定'}
          </button>
        </main>
      </MobileScreen>
    </AppLayout>
  )
}

export default EmailBindPage
